import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { ArrowRightIcon, CheckCircleIcon } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { CreateOrganizationForm } from '@/components/organization/CreateOrganizationForm';
import type { ApiOrganization } from '@envase/api/client';

export const Route = createFileRoute('/_authenticated/onboarding')({
  component: OnboardingPage,
});

function OnboardingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState(1);

  const steps = [
    { id: 1, title: t('organization.onboarding.createOrganization'), completed: false },
    { id: 2, title: t('organization.onboarding.allSet'), completed: false },
  ];

  const handleOrganizationCreated = (organization: ApiOrganization) => {
    // Move to step 2 after successful creation
    setStep(2);
  };

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-200px)]">
      <div className="max-w-2xl w-full space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">{t('organization.onboarding.welcome')}</h1>
          <p className="text-gray-600 mt-2">{t('organization.onboarding.getStarted')}</p>
        </div>

        <div className="flex items-center justify-center space-x-4">
          {steps.map((s, idx) => (
            <div key={s.id} className="flex items-center">
              <div className="flex items-center space-x-2">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    s.id === step
                      ? 'bg-purple-600 text-white'
                      : s.completed
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {s.completed ? <CheckCircleIcon className="w-5 h-5" /> : s.id}
                </div>
                <span className="text-sm font-medium text-gray-700">{s.title}</span>
              </div>
              {idx < steps.length - 1 && (
                <ArrowRightIcon className="w-5 h-5 text-gray-400 mx-4" />
              )}
            </div>
          ))}
        </div>

        {step === 1 && (
          <CreateOrganizationForm
            onSuccess={handleOrganizationCreated}
            showTitle={true}
            showDescription={true}
          />
        )}

        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>{t('organization.onboarding.allSet')}</CardTitle>
              <CardDescription>{t('organization.onboarding.createdSuccessfully')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  {t('organization.onboarding.startCreating')}
                </p>
              </div>
              <Button 
                onClick={() => navigate({ to: '/dashboard' })} 
                className="w-full"
              >
                {t('organization.onboarding.goToDashboard')}
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
